/**
 * La matrice ressource × action × rôle (5.4).
 *
 * Seule source de vérité : l'API l'applique par son intergiciel, la régie s'en
 * sert pour masquer ce qui n'est pas permis.
 *
 * Chaque règle dit trois choses : dans quelle portée elle vaut, quels rôles
 * l'obtiennent par leur rôle seul, et quel droit fin l'ouvre à quels autres
 * rôles. Les droits fins ne s'appliquent qu'aux rôles qui pourraient les
 * recevoir : le document accorde « prix artiste » et « publier » à l'Équipe,
 * pas à la Lecture ni au Comptable.
 */
const DIRECTION = ['direction'];
const DIRECTION_EQUIPE = ['direction', 'equipe'];
/** Ce que voient Direction, Équipe et Lecture : tout sauf le prix artiste. */
const VUE_GALERIE = ['direction', 'equipe', 'lecture'];
/** Ventes : le Comptable y voit les factures, les avoirs et les règlements. */
const VUE_VENTES = ['direction', 'equipe', 'lecture', 'comptable'];
/** Les quatre rôles de galerie, sans exception : la composition de l'équipe. */
const TOUTE_LA_GALERIE = ['direction', 'equipe', 'lecture', 'comptable'];
const COMPTA = ['direction', 'comptable'];
const GROUPE = ['direction_groupe', 'administration_groupe'];
const CONSOLE = ['venio_support', 'venio_exploitation', 'venio_direction'];
const CONSOLE_EXPLOITATION = ['venio_exploitation', 'venio_direction'];
/** La vue d'aigle : les trois rôles opérationnels, et l'associé (ARTO-58). */
const CONSOLE_VUE = ['venio_associe', ...CONSOLE];
/**
 * La comptabilité : la direction et l'associé, comme le partage (§ 4.6) — le
 * support et l'exploitation n'y voient rien. L'associé n'en reçoit que sa
 * ligne (`pilotage/projection.ts`).
 */
const CONSOLE_COMPTA = ['venio_associe', 'venio_direction'];
const galerie = (roles, droit) => droit === undefined ? { portee: 'galerie', roles } : { portee: 'galerie', roles, droit };
const groupe = (roles) => ({ portee: 'groupe', roles });
const console_ = (roles) => ({ portee: 'console', roles });
/** Droit fin « voir le prix artiste », accordable à l'Équipe (5.1). */
const PRIX_ARTISTE = {
    nom: 'voirPrixArtiste',
    roles: ['equipe'],
};
/** Droit fin « ouvrir le coffre confidentiel », accordable à l'Équipe (5.1). */
const COFFRE = { nom: 'coffreConfidentiel', roles: ['equipe'] };
/** Droit fin « émettre des factures », accordable à l'Équipe (5.1). */
const FACTURER = { nom: 'emettreFactures', roles: ['equipe'] };
/** Droit fin « publier le site », accordable à l'Équipe (5.1). */
const PUBLIER = { nom: 'publierSite', roles: ['equipe'] };
/** La matrice. Une entrée par action, aucune action sans entrée. */
export const MATRICE = {
    'insights:read': galerie(DIRECTION),
    'insights:write': galerie(DIRECTION),
    'dashboard:read': galerie(VUE_GALERIE),
    'artworks:read': galerie(VUE_GALERIE),
    'artworks:write': galerie(DIRECTION_EQUIPE),
    'artworks:archive': galerie(DIRECTION),
    'artworks:read-artist-price': galerie(DIRECTION, PRIX_ARTISTE),
    'artworks:import': galerie(DIRECTION_EQUIPE),
    'artworks:export': galerie(DIRECTION_EQUIPE),
    'artworks:bulk-price': galerie(DIRECTION),
    'editions:read': galerie(VUE_GALERIE),
    'editions:write': galerie(DIRECTION_EQUIPE),
    'media:read': galerie(VUE_GALERIE),
    'media:write': galerie(DIRECTION_EQUIPE),
    'registre-police:read': galerie(DIRECTION_EQUIPE),
    'registre-police:inscrire': galerie(DIRECTION_EQUIPE),
    'registre-police:rectifier': galerie(DIRECTION),
    'artists:read': galerie(VUE_GALERIE),
    'artists:write': galerie(DIRECTION_EQUIPE),
    'treasury:read': galerie(COMPTA),
    'treasury:write': galerie(COMPTA),
    'treasury:settings': galerie(DIRECTION),
    'artist-accounts:read': galerie(COMPTA, PRIX_ARTISTE),
    'artist-accounts:write': galerie(DIRECTION, PRIX_ARTISTE),
    'artist-accounts:validate': galerie(DIRECTION),
    'exhibitions:read': galerie(VUE_GALERIE),
    'exhibitions:write': galerie(DIRECTION_EQUIPE),
    // Agenda et presse (M6).
    'agenda:read': galerie(VUE_GALERIE),
    'agenda:write': galerie(DIRECTION_EQUIPE),
    'press:read': galerie(VUE_GALERIE),
    'press:write': galerie(DIRECTION_EQUIPE),
    'exhibitions:price-list': galerie(DIRECTION_EQUIPE),
    'collector-space:read': galerie(VUE_GALERIE),
    'collector-space:write': galerie(DIRECTION_EQUIPE),
    'contacts:read': galerie(VUE_GALERIE),
    'contacts:write': galerie(DIRECTION_EQUIPE),
    'contacts:export': galerie(DIRECTION),
    'contacts:erase': galerie(DIRECTION),
    'inquiries:read': galerie(VUE_GALERIE),
    'inquiries:write': galerie(DIRECTION_EQUIPE),
    'profils:read': galerie(VUE_GALERIE),
    'profils:write': galerie(DIRECTION_EQUIPE),
    'presentation:read': galerie(VUE_GALERIE),
    'presentation:write': galerie(DIRECTION_EQUIPE),
    'negociation:approuver': galerie(DIRECTION),
    'reservations:read': galerie(VUE_VENTES),
    'reservations:write': galerie(DIRECTION_EQUIPE),
    'offers:read': galerie(VUE_VENTES),
    'offers:write': galerie(DIRECTION_EQUIPE),
    'offers:send': galerie(DIRECTION_EQUIPE),
    'invoices:read': galerie(VUE_VENTES),
    'invoices:write': galerie(DIRECTION, FACTURER),
    'invoices:issue': galerie(DIRECTION, FACTURER),
    'invoices:credit': galerie(DIRECTION, FACTURER),
    'invoices:export': galerie(COMPTA),
    'online-payments:read': galerie(VUE_VENTES),
    'online-payments:write': galerie(COMPTA, FACTURER),
    'online-payments:manage': galerie(DIRECTION),
    'payments:read': galerie(VUE_VENTES),
    'payments:write': galerie(COMPTA),
    'orders:read': galerie(VUE_VENTES),
    'orders:refund': galerie(DIRECTION, FACTURER),
    'shop:write': galerie(DIRECTION),
    'insurance:read': galerie(DIRECTION, PRIX_ARTISTE),
    'insurance:write': galerie(DIRECTION, PRIX_ARTISTE),
    'insurance:settings': galerie(DIRECTION),
    'movements:read': galerie(VUE_GALERIE),
    'movements:write': galerie(DIRECTION_EQUIPE),
    'consignments:regler': galerie(DIRECTION),
    'inventaire:read': galerie(VUE_GALERIE),
    'inventaire:write': galerie(DIRECTION_EQUIPE),
    'inventaire:manage': galerie(DIRECTION),
    'locations:read': galerie(VUE_VENTES),
    'locations:write': galerie(DIRECTION_EQUIPE),
    'locations:facturer': galerie(DIRECTION, FACTURER),
    'documents:read': galerie(VUE_GALERIE),
    'documents:write': galerie(DIRECTION_EQUIPE),
    'documents:read-confidential': galerie(DIRECTION, COFFRE),
    'documents:archive': galerie(DIRECTION),
    'document-access:read': galerie(DIRECTION),
    'signatures:read': galerie(VUE_GALERIE),
    'signatures:write': galerie(DIRECTION),
    'contracts:read': galerie(DIRECTION_EQUIPE),
    'contracts:write': galerie(DIRECTION_EQUIPE),
    'contracts:templates': galerie(DIRECTION),
    'artist-contracts:read': galerie(DIRECTION_EQUIPE),
    'artist-contracts:write': galerie(DIRECTION),
    'site:read': galerie(VUE_GALERIE),
    'site:write': galerie(DIRECTION_EQUIPE),
    'site:preview': galerie(VUE_GALERIE),
    'site:publish': galerie(DIRECTION, PUBLIER),
    'posts:read': galerie(VUE_GALERIE),
    'posts:write': galerie(DIRECTION_EQUIPE),
    'pages:read': galerie(VUE_GALERIE),
    'pages:write': galerie(DIRECTION_EQUIPE),
    'viewing-rooms:read': galerie(VUE_GALERIE),
    'viewing-rooms:write': galerie(DIRECTION_EQUIPE),
    'newsletter:read': galerie(VUE_GALERIE),
    'newsletter:write': galerie(DIRECTION_EQUIPE),
    'newsletter:send': galerie(DIRECTION),
    'diffusion:read': galerie(VUE_GALERIE),
    'diffusion:write': galerie(DIRECTION_EQUIPE),
    'diffusion:manage': galerie(DIRECTION),
    // Studio.
    'studio:read': galerie(VUE_GALERIE),
    'studio:write': galerie(DIRECTION_EQUIPE),
    'ia:read': galerie(DIRECTION_EQUIPE),
    'ia:settings': galerie(DIRECTION),
    'recherche:ask': galerie(TOUTE_LA_GALERIE),
    'recherche:settings': galerie(DIRECTION),
    'settings:read': galerie(DIRECTION),
    'settings:write': galerie(DIRECTION),
    'settings:modules': galerie(DIRECTION),
    'addons:billing': galerie(DIRECTION),
    'addons:invoices': galerie(['direction', 'comptable']),
    'settings:team': galerie(DIRECTION),
    'settings:tax': galerie(DIRECTION),
    'settings:numbering': galerie(DIRECTION),
    'settings:templates': galerie(DIRECTION),
    'settings:locations': galerie(DIRECTION_EQUIPE),
    'settings:export-all': galerie(DIRECTION),
    'settings:audit': galerie(DIRECTION),
    // Lire l'équipe : TOUS les rôles de galerie, le Comptable compris. Savoir à
    // qui s'adresser dans la galerie n'expose rien du métier — et un membre qui
    // ignore qui peut facturer finit par demander à la mauvaise personne. La
    // MODIFIER reste `settings:team`, réservée à la Direction (5.1).
    'memberships:read': galerie(TOUTE_LA_GALERIE),
    'group:read': groupe(GROUPE),
    'group:read-vault-deadlines': groupe(GROUPE),
    'group:manage-galleries': groupe(['administration_groupe']),
    'group:manage-members': groupe(['administration_groupe']),
    'console:read': console_(CONSOLE),
    'console:provision-gallery': console_(CONSOLE_EXPLOITATION),
    // La reprise pose la direction d'une galerie DÉJÀ existante, qui n'en a
    // aucune : une décision de direction, comme la résiliation (incident du
    // 4 octobre 2026). La création, elle, reste à l'exploitation.
    'console:resume-provisioning': console_(['venio_direction']),
    // Dossiers d'ouverture (ARTO-127) : le support lit, l'exploitation tient le
    // dossier ; le contrat signé et le mandat de reprise restent à la direction
    // (A7 : l'associé n'y a pas accès en v1).
    'console:read-onboarding': console_(CONSOLE),
    'console:manage-onboarding': console_(CONSOLE_EXPLOITATION),
    'console:manage-mandate': console_(['venio_direction']),
    'console:manage-domains': console_(CONSOLE_EXPLOITATION),
    'console:manage-backups': console_(CONSOLE_EXPLOITATION),
    'console:export-gallery': console_(CONSOLE_EXPLOITATION),
    'console:terminate-gallery': console_(['venio_direction']),
    'console:request-support-access': console_(CONSOLE),
    'console:read-audit': console_(CONSOLE),
    'console:manage-modules': console_(CONSOLE_EXPLOITATION),
    'console:manage-addons': console_(CONSOLE_EXPLOITATION),
    'console:publish-addon': console_(['venio_direction']),
    'console:read-addon-sales': console_(CONSOLE),
    'console:manage-addon-sales': console_(CONSOLE_EXPLOITATION),
    'console:manage-addon-prices': console_(['venio_direction']),
    'console:manage-addon-billing': console_(['venio_direction']),
    // La facturation du cœur relève de la direction Venio seule.
    'console:manage-billing': console_(['venio_direction']),
    // L'interrupteur global coupe un module pour TOUTES les galeries : c'est
    // une décision de direction, comme la résiliation.
    'console:cut-ia': console_(['venio_direction']),
    'console:kill-switch': console_(['venio_direction']),
    // Un espace de test sort de la facturation et des chiffres : le marquer
    // APRÈS la création est une décision de direction (lot B). À la création,
    // la case suit le droit de provisionner.
    'console:mark-test-gallery': console_(['venio_direction']),
    // Le thème de la régie : un réglage d'exploitation, comme les modules.
    'console:manage-appearance': console_(CONSOLE_EXPLOITATION),
    // Relever la version minimale bloque les apps trop anciennes : un geste de
    // mise en production, tenu par l'exploitation comme les déploiements.
    'console:manage-app-versions': console_(CONSOLE_EXPLOITATION),
    // Vue d'aigle (ARTO-58). L'associé n'a QUE la première, et la lecture de la
    // comptabilité : il n'entre ni dans `CONSOLE` ni dans `CONSOLE_EXPLOITATION`.
    'console:read-overview': console_(CONSOLE_VUE),
    'console:manage-subscriptions': console_(CONSOLE_EXPLOITATION),
    'console:manage-receivables': console_(CONSOLE_EXPLOITATION),
    'console:manage-tariffs': console_(['venio_direction']),
    'console:manage-costs': console_(['venio_direction']),
    'console:manage-milestones': console_(['venio_direction']),
    'console:manage-associates': console_(['venio_direction']),
    'console:read-accounting': console_(CONSOLE_COMPTA),
    'console:manage-accounting': console_(['venio_direction']),
    // Reprise (ARTO-125) : l'exploitation dépose, essaie et lance les lots ; le
    // mandat contractuel, qui ouvre l'écriture dans une galerie, est posé par
    // la direction seule (avec une vérification récente du second facteur).
    'console:run-import': console_(CONSOLE_EXPLOITATION),
    // Annuler un lot (supprimer ce qu'il a créé) et sceller la reprise : la
    // direction de Venio seule, second facteur récent (ARTO-132, spec § 11).
    'console:rollback-import': console_(['venio_direction']),
    // Profils de correspondance (ARTO-130) : un outil de l'exploitation, sans
    // donnée de galerie (spec § 11).
    'console:manage-import-profiles': console_(CONSOLE_EXPLOITATION),
    // Prise de rendez-vous en ligne : la configuration reste à la direction.
    'creneaux:read': galerie(VUE_GALERIE),
    'creneaux:write': galerie(DIRECTION_EQUIPE),
    'creneaux:configurer': galerie(DIRECTION),
    // Le comptable n'a pas l'usage du pipeline (décision du 13 octobre 2026).
    'pipeline:read': galerie(VUE_GALERIE),
    'pipeline:write': galerie(DIRECTION_EQUIPE),
    'inbox:read': galerie(VUE_GALERIE),
    'inbox:write': galerie(DIRECTION_EQUIPE),
    'inbox:settings': galerie(DIRECTION),
    'events:read': galerie(VUE_GALERIE),
    'events:write': galerie(DIRECTION_EQUIPE),
    'events:checkin': galerie(DIRECTION_EQUIPE),
};
//# sourceMappingURL=matrice.js.map